import { CohLogo } from "@/assets/CohLogo";
import { CohLogoAr } from "@/assets/CohLogo-ar";
import i18n from "@/i18n";
import { bgOverlayStyle, isRTLLanguage } from "@/utils/helpers";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CircleForm from "./steps/CircleForm";
import { ThankYouStep } from "./steps/ThankYouStep";
import { useTour } from "@/hooks/useTour";

interface CircleCreationContainerProps {
  readonly children?: React.ReactNode;
  image?: File | null;
  title: string;
}

const CircleCreationContainer = ({ children, image, title }: CircleCreationContainerProps) => {
  const [objectURL, setObjectURL] = useState<string | null>(null);

  const imageUrl = useMemo(() => {
    if (image?.size) {
      const url = URL.createObjectURL(image);
      setObjectURL(url);
      return bgOverlayStyle(url);
    }
    return bgOverlayStyle("/creation-overlay.png");
  }, [image]);
  useEffect(() => {
    return () => {
      if (objectURL) {
        URL.revokeObjectURL(objectURL);
      }
    };
  }, [objectURL]);
  return (
    <main className="flex h-[calc(100dvh-56px)] w-full flex-col overflow-hidden sm:flex-row">
      <div
        className="relative z-20 h-[30%] w-full sm:h-full sm:w-2/5 md:h-full lg:h-full"
        style={{
          backgroundImage: imageUrl,
          backgroundPosition: "center center",
          backgroundSize: "cover",
        }}
      >
        <div className="dark flex h-16 w-full items-start justify-center sm:h-12 sm:w-12 sm:p-4 md:block">
          <Link to="/coh" aria-label="Go to circle of humanity" className="z-50 hidden md:block">
            {i18n.language === "ar" ? <CohLogoAr /> : <CohLogo />}
          </Link>
          <div className="flex-grow"></div>
        </div>
        {image && (
          <div>
            <h1 className="absolute bottom-8 start-6 text-3xl font-bold text-[#EDEAE4]">{title}</h1>
          </div>
        )}
      </div>
      {children}
    </main>
  );
};

const CircleCreation = () => {
  const { t } = useTranslation();
  const { tourState, setMockData, setRealCircleId, nextStep } = useTour();
  const [image, setImage] = useState<File | null>(null);
  const [title, setTitle] = useState<string>("");
  const [currentStep, setCurrentStep] = useState(1);
  const [circleId, setCircleId] = useState<string | null>(null);

  // Handle tour mode
  useEffect(() => {
    if (tourState.isActive) {
      // If we're in tour mode and on step 3 (thank you step), show step 2
      if (tourState.currentStep === 3) {
        setCurrentStep(2);
        // Use the real circle ID that was created
        if (tourState.realCircleId) {
          setCircleId(tourState.realCircleId);
        }
        setTitle(tourState.mockData.circleTitle || "My Amazing Circle");
        setImage(tourState.mockData.circleImage);
      } else {
        setCurrentStep(1);
      }
    }
  }, [tourState]);

  const handleCircleCreated = (
    newCircleId: string,
    circleTitle: string,
    circleImage: File | null,
  ) => {
    if (tourState.isActive) {
      // In tour mode, save the real data and use real circle ID
      setMockData({
        circleTitle,
        circleImage,
      });
      // Store the real circle ID for the tour
      setRealCircleId(newCircleId);
      // Use the real circle ID from the API response
      setCircleId(newCircleId);
      setTitle(circleTitle);
      setImage(circleImage);
      setCurrentStep(2);
      // Move to next tour step (step 3: thank you step)
      nextStep();
    } else {
      // Normal mode
      setCircleId(newCircleId);
      setCurrentStep(2);
    }
  };

  return (
    <>
      <div className="flex h-14 items-center justify-between bg-foreground px-4 md:px-[24px]">
        <div className="flex flex-row  gap-1 text-xs text-white md:text-sm">
          {t("poweredBy.first_part")}{" "}
          <Link aria-label={t("poweredBy.first_part") + " " + t("poweredBy.second_part")} to="/">
            <p className="font-bold text-white underline"> {t("poweredBy.second_part")}</p>
          </Link>
        </div>
        <Link aria-label={t("poweredBy.first_part") + " " + t("poweredBy.second_part")} to="/">
          {isRTLLanguage(i18n.language) ? (
            <img src="/efb-logo-ar-white.svg" className="h-8" />
          ) : (
            <img src="/efb-logo-white.svg" className="h-8" />
          )}
        </Link>
      </div>
      <CircleCreationContainer image={image} title={title}>
        <div className="h-[70%] flex-1 overflow-hidden rounded-t-2xl bg-background px-4 pt-6 sm:h-full sm:px-5 sm:pt-3 ">
          <div className=" flex h-full flex-col items-center bg-background">
            {currentStep === 1 && (
              <CircleForm
                currentStep={currentStep}
                setCurrentStep={setCurrentStep}
                setImage={setImage}
                setTitle={setTitle}
                setCircleId={setCircleId}
                image={image}
                onCircleCreated={handleCircleCreated}
              />
            )}
            {currentStep === 2 && <ThankYouStep circleId={circleId} currentStep={currentStep} />}
          </div>
        </div>
      </CircleCreationContainer>
    </>
  );
};

export default CircleCreation;
